import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Mountain, ArrowRight, MapPin, Compass, Home as HomeIcon } from "lucide-react";
import api from "../lib/api";
import Seo from "../components/Seo";
import { BUSINESS, aptUrl } from "../lib/site";

export default function NotFound() {
  const [apartments, setApartments] = useState([]);

  useEffect(() => {
    api.get("/apartments").then((r) => setApartments(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="bg-ivory min-h-screen flex flex-col">
      <Seo title="Pagina non trovata (404)" description="La pagina che cerchi non esiste o è stata spostata. Torna alla home di Appartamenti in Val di Sole o scopri i nostri appartamenti in Val di Sole." path="/404" noindex />
      <Helmet>
        <meta name="prerender-status-code" content="404" />
      </Helmet>

      <header className="sticky top-0 z-30 bg-fir-deep/95 backdrop-blur text-ivory px-6 md:px-16 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2"><Mountain className="text-gold" size={24} /><span className="font-display text-xl">Appartamenti in Val di Sole</span></Link>
        <Link to="/#strutture" className="gold-btn px-4 py-2 rounded-xl text-sm">Verifica disponibilità</Link>
      </header>

      <main className="flex-1 flex items-center px-6 py-16">
        <div className="max-w-4xl mx-auto w-full text-center">
          <p className="font-display text-[6rem] sm:text-[8rem] leading-none text-gold/90 select-none">404</p>
          <h1 className="font-display text-4xl sm:text-5xl text-fir mt-2">Ops! Questa pagina si è persa tra le vette</h1>
          <p className="text-fir-deep/70 mt-4 max-w-xl mx-auto leading-relaxed">La pagina che stai cercando non esiste o è stata spostata. Ma la tua vacanza in Val di Sole ti aspetta: riparti da qui.</p>

          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Link to="/" data-testid="notfound-home-btn" className="gold-btn px-7 py-3.5 rounded-xl inline-flex items-center gap-2"><HomeIcon size={18} /> Torna alla Home</Link>
            <Link to="/#strutture" className="border border-fir text-fir hover:bg-fir hover:text-ivory px-7 py-3.5 rounded-xl inline-flex items-center gap-2 transition-colors"><Compass size={18} /> Le nostre strutture</Link>
          </div>

          {apartments.length > 0 && (
            <div className="mt-14 text-left">
              <p className="text-wood uppercase tracking-[0.2em] text-sm mb-5 text-center">Forse cercavi una di queste</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {apartments.slice(0, 6).map((a) => (
                  <Link key={a.id} to={aptUrl(a)} data-testid="notfound-apartment-link"
                    className="bg-white rounded-2xl border border-stone p-5 hover:shadow-lg hover:border-gold/40 transition-all group flex items-center justify-between gap-3">
                    <span className="font-display text-lg text-fir group-hover:text-gold transition-colors">{a.title}</span>
                    <ArrowRight size={17} className="text-gold shrink-0 group-hover:translate-x-1 transition-transform" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="bg-fir-deep text-ivory px-6 py-10">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2"><Mountain className="text-gold" size={24} /><span className="font-display text-2xl">Appartamenti in Val di Sole</span></div>
          <div className="text-ivory/60 text-sm flex items-center gap-2"><MapPin size={15} className="text-gold" /> {BUSINESS.locality}, Val di Sole (TN) · {BUSINESS.phone}</div>
        </div>
        <div className="max-w-5xl mx-auto mt-4 flex flex-wrap gap-4 text-sm">
          <Link to="/guide" className="text-ivory/50 hover:text-gold">Guide</Link>
          <Link to="/privacy" className="text-ivory/50 hover:text-gold">Privacy</Link>
          <Link to="/cookie-policy" className="text-ivory/50 hover:text-gold">Cookie Policy</Link>
        </div>
      </footer>
    </div>
  );
}
